import { createContext, useContext, ReactNode, useState } from "react";
import { School } from "../types/School";
import { Principal } from "../types/Principal";
import { PlacementQuestion } from "../types/PlacementQuestion";
import DbService from "../services/DbService";
import { useHandleError } from "../hooks/useHandleError";
import { Teacher } from "../types/TeacherModel";

interface AdminContextType {
  schools: School[];
  setSchoolsToAdminProvider: (schools: School[]) => void;
  schoolsWithoutPrincipal: School[];
  principals: Principal[];
  setPrincipalsToAdminProvider: (principals: Principal[]) => void;
  principalsWithoutSchool: Principal[];
  refreshPrincipalList: () => Promise<void>;
  refreshSchoolList: () => Promise<void>;
  refreshSchoolAndPrincipalList: () => Promise<void>;
  placementQuestions: PlacementQuestion[];
  setPlacementQuestions: React.Dispatch<
    React.SetStateAction<PlacementQuestion[]>
  >;
  updatePrincipalList: (updatedPrincipal: Principal) => void;
  updateSchoolList: (updatedSChool: School) => void;
  teachers: Teacher[];
  setTeachersToAdminProvider: (teachers: Teacher[]) => void;
  refreshTeacherList: () => Promise<void>;
  updateTeacherList: (updatedTeacher: Teacher) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within a AdminProvider");
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const handleError = useHandleError();
  const [schools, setSchools] = useState<School[]>([]);
  const [schoolsWithoutPrincipal, setSchoolsWithoutPrincipal] = useState<
    School[]
  >([]);
  const [principals, setPrincipals] = useState<Principal[]>([]);
  const [principalsWithoutSchool, setPrincipalsWithoutSchool] = useState<
    Principal[]
  >([]);

  const [placementQuestions, setPlacementQuestions] = useState<
    PlacementQuestion[]
  >([]);

  const [teachers, setTeachers] = useState<Teacher[]>([]);

  const updatePrincipalList = (updatedPrincipal: Principal) => {
    setPrincipals(() => {
      let filteredPrincipals: Principal[] = [];
      const updatedPrincipals = principals.map((p) => {
        if (!p.schoolId && p.isActive == true) {
          filteredPrincipals.push(p);
        }
        return p._id == updatedPrincipal._id ? updatedPrincipal : p;
      });

      setPrincipalsWithoutSchool(filteredPrincipals);
      return updatedPrincipals;
    });
  };

  const updateTeacherList = (updatedTeacher: Teacher) => {
    setTeachers((prevTeachers) => {
      return prevTeachers.map((t) =>
        t._id === updatedTeacher._id ? updatedTeacher : t
      );
    });
  };

  const updateSchoolList = (updatedSchool: School) => {
    setSchools(() => {
      let filteredSchools: School[] = [];
      const updatedSchools = schools.map((s) => {
        if (!s.principalId && s.isActive == true) {
          filteredSchools.push(s);
        }
        return s._id == updatedSchool._id ? updatedSchool : s;
      });

      setSchoolsWithoutPrincipal(filteredSchools);
      return updatedSchools;
    });
  };

  const setSchoolsToAdminProvider = (schools: School[]) => {
    setSchools((prevSchools) => {
      const newSchools = [...schools];
      const filteredSchools = newSchools.filter(
        (s) => !s.principalId && s.isActive == true
      );
      setSchoolsWithoutPrincipal(filteredSchools);
      return newSchools;
    });
  };

  const setPrincipalsToAdminProvider = (principals: Principal[]) => {
    setPrincipals((prevPrincipals) => {
      const newPrincipals = [...principals];
      const filteredPrincipals = newPrincipals.filter(
        (p) => !p.schoolId && p.isActive == true
      );
      setPrincipalsWithoutSchool(filteredPrincipals);
      return newPrincipals;
    });
  };

  const setTeachersToAdminProvider = (teachers: Teacher[]) => {
    setTeachers(teachers);
  };

  const refreshTeacherList = async () => {
    try {
      const teacherResponse = await DbService.getTeachers();
      setTeachersToAdminProvider(teacherResponse.data);
    } catch (err: any) {
      handleError(err);
    }
  };

  const refreshSchoolAndPrincipalList = async () => {
    try {
      await refreshSchoolList();
      await refreshPrincipalList();
    } catch (err: any) {
      handleError(err);
    }
  };

  const refreshPrincipalList = async () => {
    try {
      const principalResponse = await DbService.getPrincipals();
      setPrincipalsToAdminProvider(principalResponse.data);
    } catch (err: any) {
      handleError(err);
    }
  };

  const refreshSchoolList = async () => {
    try {
      const schoolResponse = await DbService.getSchools();
      setSchoolsToAdminProvider(schoolResponse.data);
    } catch (err: any) {
      handleError(err);
    }
  };

  return (
    <AdminContext.Provider
      value={{
        schools,
        setSchoolsToAdminProvider,
        schoolsWithoutPrincipal,
        principals,
        setPrincipalsToAdminProvider,
        principalsWithoutSchool,
        refreshSchoolAndPrincipalList,
        refreshPrincipalList,
        refreshSchoolList,
        placementQuestions,
        setPlacementQuestions,
        updatePrincipalList,
        updateSchoolList,
        teachers,
        setTeachersToAdminProvider,
        refreshTeacherList,
        updateTeacherList,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
